/* ---------- CP-002's pump 2 back-up delay -KF22 (#3844, the sizing engineer's spec) ----------
 * On the back-up run pump 2 now starts through -KF22's delayed closer 15-18, 3 s after pump 1, and -KF22's coil is fed through -KF3's
 * closer 41-44 on page 5. -KF3's 31-34 is spare. A save made before holds page 5 drawn without -KF22, which the review now fails
 * (CP2-03), and a panel with -KF3's 31-34 in pump 2's path and no -KF22, which the wiring step would now call wrong. How such a save
 * is read: the save migration in packages/progress uses this (format 39). Page 7 is drawn ready by the page itself, not saved.
 * Self-contained on purpose, as deltafeedback.ts: the migration imports this file alone, not CP-002's pack. */

/** Page 5's two new elements, where page5() places them: the dry-running float's column (pages.ts PATH_X[0]), rows a and coil. */
export const CP002_STAGGER_PARTS = { staggerStart: { x: 154, y: 160 }, staggerCoil: { x: 154, y: 370 } };
/** Page 5's three new wires, as page5()'s steps draw them. */
export const CP002_STAGGER_DRAWN: [string, string][] = [["+24V dry", "KF3:41"], ["KF3:44", "KF22:A1"], ["KF22:A2", "0V"]];
/** In the panel: -KF22 on rail 3 this far right of -KF4 (layout.ts), and every device on rail 3 right of -KF4 this much further. */
export const CP002_STAGGER_AFTER_KF4 = 0.0238, CP002_STAGGER_SHIFT = 0.01485;
/** In the panel: conductor ends that move, the conductors a new terminal goes between (A~B becomes A~X and X~B, on the same net), and
 *  the new conductor -KF3:44 to -KF22:A1. As wiring.ts lists them. */
export const CP002_STAGGER_MOVED: Record<string, string> = { "KF3:31": "KF22:15", "KF3:34": "KF22:18" };
export const CP002_STAGGER_BETWEEN: [string, string, string][] = [["KF3:11", "SF11:13", "KF3:41"], ["PF1:X2", "QA12:A2", "KF22:A2"]];
export const CP002_STAGGER_NEW: [string, string] = ["KF3:44", "KF22:A1"];

const has = (ws: any[][], a: string, b: string) => ws.some(w => (w?.[0] === a && w?.[1] === b) || (w?.[0] === b && w?.[1] === a));

/** Page 5 as drawn: where the back-up run relay's coil is placed, -KF3's closer 41-44 and -KF22's coil placed and their wires drawn. */
function page5(d: any): any {
  if (!d || typeof d !== "object" || !d.parts?.backupCoil || d.parts.staggerCoil) return d;
  const wires: any[] = Array.isArray(d.wires) ? d.wires : [];
  return { ...d, parts: { ...d.parts, ...CP002_STAGGER_PARTS }, wires: [...wires, ...CP002_STAGGER_DRAWN.filter(([a, b]) => !has(wires, a, b))] };
}

const at = (end: unknown) => typeof end === "string" ? (CP002_STAGGER_MOVED[end] ?? end) : end;
/** A build record ({ placements, wires }) where -KF4 is placed and -KF22 not: -KF22 placed after -KF4 and the devices right of it
 *  moved along, the conductors on -KF3's 31-34 moved to -KF22's 15-18 (size, colour and mark kept), -KF3:41 and -KF22:A2 looped into
 *  their nets, and -KF22's coil conductor added after -KF3:41's where -KF3 is wired; the wiring step gives the new one the list's size, colour and mark when it loads. */
function build(b: any): any {
  const placements: any[] = Array.isArray(b?.placements) ? b.placements : [], kf4 = placements.find(p => p?.tag === "KF4");
  if (!kf4 || placements.some(p => p?.tag === "KF22")) return b;
  const moved = placements.map(p => p?.rail === kf4.rail && p?.tag !== "KF4" && typeof p?.x === "number" && p.x > kf4.x ? { ...p, x: +(p.x + CP002_STAGGER_SHIFT).toFixed(5) } : p);
  const k = moved.indexOf(moved.find(p => p?.tag === "KF4"));
  const next = [...moved.slice(0, k + 1), { tag: "KF22", rail: kf4.rail, x: +(kf4.x + CP002_STAGGER_AFTER_KF4).toFixed(5) }, ...moved.slice(k + 1)];
  let wires: any[] = (Array.isArray(b?.wires) ? b.wires : []).map((w: any) => {
    const from = at(w?.from), to = at(w?.to);
    return from === w?.from && to === w?.to ? w : { ...w, from, to };
  });
  for (const [a, c, x] of CP002_STAGGER_BETWEEN) {
    const i = wires.findIndex(w => (w?.from === a && w?.to === c) || (w?.from === c && w?.to === a)); if (i < 0) continue;
    const w = wires[i], [p, q] = w.from === a ? [a, c] : [c, a];
    wires = [...wires.slice(0, i), { ...w, from: p, to: x }, { ...w, from: x, to: q }, ...wires.slice(i + 1)];
  }
  // the coil's conductor beside -KF3:41's two, and only in a panel whose -KF3 is wired already: one only placed keeps its wiring to do
  const [a, c] = CP002_STAGGER_NEW, wired = wires.some(w => /^KF3:/.test(w?.from) || /^KF3:/.test(w?.to));
  if (wired && !wires.some(w => (w?.from === a && w?.to === c) || (w?.from === c && w?.to === a))) {
    const i = wires.findIndex(w => w?.from === "KF3:41" || w?.to === "KF3:41"), j = i < 0 ? wires.length : i + 2;
    wires = [...wires.slice(0, j), { from: a, to: c }, ...wires.slice(j)];
  }
  return { ...b, placements: next, wires };
}

const one = <A extends { ui?: any; model?: any }>(s: A): A => {
  let out: any = s;
  const d = s?.model?.drawings?.["5"];
  if (d) { const p = page5(d); if (p !== d) out = { ...out, model: { ...out.model, drawings: { ...out.model.drawings, 5: p } } }; }
  if (s?.ui?.build) { const b = build(s.ui.build); if (b !== s.ui.build) out = { ...out, ui: { ...out.ui, build: b } }; }
  return out;
};
/** A saved CP-002 project with pump 2's back-up delay -KF22 on page 5 and in the panel: its own work and each earlier attempt it keeps
 *  ("Start again", #1220). */
export function staggerCp002Save<T extends { ui?: any; model?: any; attempts?: any[] }>(s: T): T {
  const out = one(s);
  return Array.isArray(s?.attempts) ? { ...out, attempts: s.attempts.map(a => one(a)) } : out;
}
